import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import InfoTooltip from '../utility/InfoTooltip';
import ProvideFeedbackButton from './ProvideFeedbackButton';
import Show from '../utility/Show';

const theme = createTheme();

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => (order === 'desc'
  ? (a, b) => descendingComparator(a, b, orderBy)
  : (a, b) => -descendingComparator(a, b, orderBy));

const stableSort = (array, comparator, orderBy) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    if (orderBy === 'speed') {
      // String parsing for UP/DOWN internet speed
      const first = [{ ...a[0] }];
      const second = [{ ...b[0] }];
      first[0].speed = first[0].speed.split('/')[0] + first[0].speed.split('/')[1];
      second[0].speed = second[0].speed.split('/')[0] + second[0].speed.split('/')[1];
      const order = comparator(first[0], second[0]);
      if (order !== 0) {
        return order;
      }
      return first[1] - second[1];
    }
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const headCells = [
  {
    id: 'provider',
    numeric: false,
    label: 'Provider',
  },
  {
    id: 'tech',
    numeric: false,
    label: 'Tech',
  },
  {
    id: 'speed',
    numeric: false,
    label: 'Speed (Mbps)',
  },
  {
    id: 'coverage',
    numeric: true,
    label: 'Coverage',
  },
];

const EnhancedTableHead = (props) => {
  const { order, orderBy, onRequestSort } = props;
  const selection = useSelector((state) => state.mainControls.selection);
  const [filteredHeadCells, setFilteredHeadCells] = useState([]);
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  useEffect(() => {
    if (!selection) {
      setFilteredHeadCells([
        {
          id: 'provider',
          numeric: false,
          label: 'Provider',
        },
        {
          id: 'tech',
          numeric: false,
          label: 'Tech',
        },
        {
          id: 'speed',
          numeric: true,
          label: 'Speed (Mbps)',
        },
      ]);
    } else {
      setFilteredHeadCells(headCells);
    }
  }, [selection]);

  return (
    <TableHead>
      <TableRow>
        {filteredHeadCells.map((headCell) => (
          <TableCell
            sx={{ background: '#ddd', color: 'black' }}
            key={headCell.id}
            align={headCell.id === 'provider' ? 'left' : 'right'}
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <ThemeProvider theme={theme}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </ThemeProvider>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const EnhancedTableToolbar = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '5px',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <InfoTooltip
        content={(
          <div style={{ whiteSpace: 'pre-line' }}>
            <strong style={{ fontSize: 20 }}>About the Provider Table</strong>
            <br />
            Some providers may not serve the entire selected area.
            <br />
            Coverage and speed is self-reported by providers.
          </div>
        )}
      />
      <Typography
        sx={{ flex: '1 1 100%', marginLeft: '5px' }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Broadband Providers
      </Typography>
    </div>
    <ProvideFeedbackButton />
  </div>
);

const ProviderTable = ({ mobile }) => {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('coverage');
  const selectionProviders = useSelector((state) => state.mainControls.selection?.providers);
  const statewideDataProviders = useSelector((state) => state.statewideData?.feature?.properties?.providers);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const providers = selectionProviders || statewideDataProviders;

  if (providers) {
    // const filteredProviders = providers.filter((p) => p.coverage >= 0.001);

    return (
      <div
        style={{
          width: '100%',
          marginBottom: mobile ? 0 : 20,
          background: '#22467F',
          borderRadius: 5,
          padding: 10,
          overflowY: 'hidden',
          height: mobile ? '' : '50%',
        }}
      >
        <EnhancedTableToolbar />
        <TableContainer sx={{ maxHeight: mobile ? '500px' : '350px', minHeight: mobile ? '300px' : '250px', paddingBottom: '60px', root: { width: 'max-content' } }}>
          <Table
            stickyHeader
            sx={{ backgroundColor: '#fff' }}
            aria-labelledby="tableTitle"
            size="small"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              selectionProviders={selectionProviders}
            />
            <TableBody>
              {stableSort(providers, getComparator(order, orderBy), orderBy)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      tabIndex={-1}
                      key={row.provider + row.tech + row.speed}
                      sx={{ maxHeight: mobile ? 40 : '' }}
                    >
                      <TableCell
                        sx={{ color: 'black' }}
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="normal"
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <p style={{ margin: 0 }}>{row.provider}</p>
                          <Show test={row.note}>
                            <InfoTooltip content={row.note} table />
                          </Show>
                        </div>
                      </TableCell>
                      <TableCell sx={{ color: 'black' }} align="right">{row.tech}</TableCell>
                      <TableCell sx={{ color: 'black' }} align="right">{row.speed}</TableCell>
                      <Show test={selectionProviders}>
                        <TableCell sx={{ color: 'black' }} align="right">{`${((Math.floor(row.coverage * 1000)) / 10).toFixed(1)}%`}</TableCell>
                      </Show>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }
  return (
    <div
      style={{
        width: '100%',
        height: 329,
        marginBottom: 20,
        background: '#22467F',
        borderRadius: 5,
      }}
    />
  );
};

export default ProviderTable;
